import React, { useState } from "react";
import { Briefcase, Home, Sparkles, Heart, ArrowRight, Search, CalendarCheck, Video, LayoutGrid } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import { translations } from "../../i18n/translations";
import { CATEGORIES, RESULTS_IMAGES } from "../../mock/mock";
import { mediaUrl } from "../../lib/media";
import { useReveal } from "../../hooks/useReveal";

const CAT_ICONS = { career: Briefcase, decor: Home, beauty: Sparkles, wellness: Heart };
const CAT_IMG = {
  career: RESULTS_IMAGES[0], decor: RESULTS_IMAGES[1], beauty: RESULTS_IMAGES[4], wellness: RESULTS_IMAGES[2],
};

export const CategoriesSection = ({ onPick, categories }) => {
  const { lang, loc, t } = useLang();
  const [ref, inView] = useReveal();
  const list = categories && categories.length ? categories : CATEGORIES;
  return (
    <section id="categories" className="py-16 sm:py-24 bg-neutral-50">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div ref={ref} className={`reveal ${inView ? "in-view" : ""} text-center max-w-2xl mx-auto`}>
          <h2 className="font-display font-extrabold text-3xl sm:text-5xl text-neutral-900 tracking-tight">{t("sections.categoriesTitle")}</h2>
          <p className="mt-3 text-neutral-500 text-lg">{t("sections.categoriesSub")}</p>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {list.map((c) => {
            const Icon = CAT_ICONS[c.key || c.id] || LayoutGrid;
            const img = mediaUrl(c.image) || CAT_IMG[c.key || c.id] || RESULTS_IMAGES[0];
            const name = (c.name && loc(c.name)) || translations[lang]?.categories?.[c.key || c.id] || c.id;
            return (
              <button
                key={c.id}
                onClick={() => onPick(c.id)}
                className="group relative overflow-hidden rounded-3xl aspect-[4/5] text-start card-hover"
              >
                <img src={img} alt={name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${c.color || "#111"}ee, ${c.color || "#111"}55 55%, transparent)` }} />
                <div className="relative h-full flex flex-col justify-end p-5 text-white">
                  <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-3" style={{ background: c.accent || "#6366f1" }}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-display font-bold text-xl">{name}</h3>
                  <span className="mt-1 inline-flex items-center gap-1 text-sm text-white/80 group-hover:gap-2 transition-all">
                    {t("sections.viewAll")} <ArrowRight className="w-4 h-4 rtl:rotate-180" />
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const HowItWorks = () => {
  const { t } = useLang();
  const [ref, inView] = useReveal();
  const steps = [
    { icon: Search, title: { tr: "Uzmanını bul", en: "Find your expert", ru: "Найдите эксперта", ar: "اعثر على خبيرك" }, desc: { tr: "Kategori veya isimle ara, sana uygun uzmanı seç.", en: "Search by category or name, pick the right one.", ru: "Ищите по категории или имени.", ar: "ابحث حسب الفئة أو الاسم." } },
    { icon: CalendarCheck, title: { tr: "Randevu al", en: "Book a session", ru: "Забронируйте", ar: "احجز جلسة" }, desc: { tr: "Uygun tarih ve saati seç, saniyeler içinde onayla.", en: "Pick a date & time, confirm in seconds.", ru: "Выберите дату и время.", ar: "اختر التاريخ والوقت." } },
    { icon: Video, title: { tr: "Görüşmeye katıl", en: "Join the call", ru: "Присоединяйтесь", ar: "انضم للمكالمة" }, desc: { tr: "Görüntülü görüşmede birebir tavsiye al.", en: "Get 1-on-1 advice over video.", ru: "Получите совет 1 на 1.", ar: "احصل على نصيحة فردية." } },
  ];
  const { loc } = useLang();
  return (
    <section id="how" className="py-16 sm:py-24">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div ref={ref} className={`reveal ${inView ? "in-view" : ""} grid md:grid-cols-3 gap-8`}>
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="relative text-center">
                <div className="mx-auto w-16 h-16 rounded-3xl bg-neutral-900 flex items-center justify-center mb-5">
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <div className="absolute top-0 start-1/2 -translate-x-1/2 rtl:translate-x-1/2 text-[80px] font-display font-extrabold text-neutral-100 -z-10 leading-none">{i + 1}</div>
                <h3 className="font-display font-bold text-xl text-neutral-900">{loc(s.title)}</h3>
                <p className="mt-2 text-neutral-500">{loc(s.desc)}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const ResultsSection = ({ images }) => {
  const { t } = useLang();
  const [ref, inView] = useReveal();
  const list = (images && images.length) ? images : RESULTS_IMAGES;
  return (
    <section className="py-16 sm:py-24 bg-neutral-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div ref={ref} className={`reveal ${inView ? "in-view" : ""} text-center max-w-2xl mx-auto mb-12`}>
          <h2 className="font-display font-extrabold text-3xl sm:text-5xl text-neutral-900 tracking-tight text-balance">{t("sections.results")}</h2>
          <p className="mt-3 text-neutral-500 text-lg">{t("sections.resultsSub")}</p>
        </div>
        <div className="columns-2 md:columns-3 gap-4 [column-fill:_balance]">
          {list.map((src, i) => (
            <div key={i} className="mb-4 break-inside-avoid rounded-3xl overflow-hidden card-hover">
              <img src={mediaUrl(src)} alt="" loading="lazy" className="w-full object-cover" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const MissionSection = ({ content }) => {
  const { t, lang } = useLang();
  const [ref, inView] = useReveal();
  const customMission = content?.[lang]?.mission;
  return (
    <section id="about" className="py-20 sm:py-28">
      <div ref={ref} className={`reveal ${inView ? "in-view" : ""} max-w-4xl mx-auto px-5 sm:px-8 text-center`}>
        <span className="inline-block px-4 py-1.5 rounded-full bg-indigo-50 text-indigo-600 text-sm font-semibold mb-6">✦</span>
        <h2 className="font-display font-extrabold text-3xl sm:text-6xl text-neutral-900 tracking-tight leading-tight text-balance">
          {customMission || t("sections.mission")}
        </h2>
        <p className="mt-6 text-lg sm:text-xl text-neutral-500 leading-relaxed text-balance">{t("sections.missionText")}</p>
      </div>
    </section>
  );
};

export const InvestorsSection = ({ investors }) => {
  const { t, loc } = useLang();
  const [ref, inView] = useReveal();
  const row = [...investors, ...investors];
  return (
    <section className="py-16 sm:py-20 bg-neutral-900 text-white overflow-hidden">
      <div ref={ref} className={`reveal ${inView ? "in-view" : ""} max-w-7xl mx-auto px-5 sm:px-8 text-center mb-10`}>
        <h2 className="font-display font-extrabold text-2xl sm:text-4xl tracking-tight">{t("sections.investors")}</h2>
        <p className="mt-3 text-neutral-400">{t("sections.investorsSub")}</p>
      </div>
      <div className="relative">
        <div className="flex gap-4 w-max marquee-track">
          {row.map((inv, i) => (
            <div key={i} className="shrink-0 w-56 px-6 py-5 rounded-2xl bg-neutral-800 border border-neutral-700 flex items-center gap-3">
              {mediaUrl(inv.image) ? (
                <img src={mediaUrl(inv.image)} alt={inv.name} className="w-11 h-11 rounded-xl object-cover shrink-0 bg-neutral-700" />
              ) : (
                <div className="w-11 h-11 rounded-xl bg-neutral-700 flex items-center justify-center font-display font-bold text-lg shrink-0">{inv.name?.[0]}</div>
              )}
              <div className="min-w-0">
                <div className="font-display font-bold text-base truncate">{inv.name}</div>
                <div className="text-sm text-neutral-400 mt-0.5 truncate">{loc(inv.role)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
